import { useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { v4 } from 'uuid';
import { useWebSocket } from './useWebSocket';
import { handleWebsocketMessageThunk } from 'components/ChatDialog/store/thunks/handleWebsocketMessage';
import { setChatWebsocket } from 'components/ChatDialog/store/slices/chatSlice';
import { getProjectThreadsThunk } from 'components/ChatDialog/store/thunks/getProjectThreads';
import { selectProjectsArray } from 'store/projects/selectors/projectsSelectors';
import isFeatureEnabled, { Feature } from 'utils/isFeatureEnabled';
import { createChatSocketUrl } from 'utils/createChatSocketUrl';
import {
  isReconnectRequest,
  RECONNECT_CLOSE_CODE,
} from 'utils/chatSocketReconnect';

// A flapping connection can reopen repeatedly; the backoff bounds that, but a
// short floor keeps a burst of reconnects from refetching every thread each time.
const RESYNC_COOLDOWN_MS = 15000;

/**
 * Custom hook for managing chat WebSocket connection with automatic resubscription
 * @returns {Object} - { chatWebsocketRef, storeThreads }
 */
export const useChatWebSocket = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.root.user);
  // Read projects at reconnect time instead of subscribing to them: this hook
  // is mounted in App and has no reason to re-render when a project changes.
  const store = useStore();
  const chatThreadsRef = useRef([]);
  const hasConnectedRef = useRef(false);
  const lastResyncAtRef = useRef(0);

  const subscribeToChatThreads = (ws, threads) => {
    if (!ws || !threads.length) return;

    // A reconnect mints a new connectionId, so every thread is registered from
    // scratch here. Sending isActive:false for all of them dropped the "I am
    // looking at this thread" flag on the one the user actually has open, and
    // nothing put it back: ChatDialog's own subscription effect only re-fires
    // when the thread or the dialog's open state changes, and neither does
    // across a reconnect. Broadcasts aimed at active connections only — the
    // typing indicator, read receipts — then silently stopped reaching them.
    const { thread, isChatDialogOpen } = store.getState().chatSlice;
    const activeThreadId = isChatDialogOpen
      ? thread?.threadId || thread?.id
      : null;

    threads.forEach((chatThread) => {
      const socketValidObject = {
        newThreadConnection: {
          id: v4(),
          userId: user.id,
          threadId: chatThread.id,
          isActive: chatThread.id === activeThreadId,
        },
      };
      ws.send(JSON.stringify(socketValidObject));
    });
  };

  // Resubscribing restores the *flow* of events but recovers nothing that was
  // broadcast while the socket was down — those frames are gone, and nothing
  // else reconciles them. An ongoing call that ended during a dropped
  // connection would otherwise show as ongoing indefinitely. Refetching the
  // project threads re-seeds both the active calls and the unread counts from
  // server truth.
  const resyncAfterReconnect = () => {
    const now = Date.now();
    if (now - lastResyncAtRef.current < RESYNC_COOLDOWN_MS) return;
    lastResyncAtRef.current = now;

    const projects = selectProjectsArray(store.getState()).filter((project) =>
      isFeatureEnabled(Feature.PROJECT_CHAT, project)
    );
    if (!projects.length) return;

    dispatch(
      getProjectThreadsThunk({
        projects,
        chatWebsocketRef,
        userId: user.id,
      })
    ).then((result) => {
      const threads = result.payload?.threads;
      if (threads) {
        chatThreadsRef.current = Object.values(threads);
      }
    });
  };

  const { wsRef: chatWebsocketRef } = useWebSocket({
    url: process.env.REACT_APP_CHAT_WEBSOCKET_URL,
    // The socket's $connect admits only a URL carrying a ticket the API minted
    // for this user, and takes the user from it: the userId in the frames
    // above is not read any more. One ticket per attempt.
    getUrl: createChatSocketUrl,
    enabled: !!user?.id,
    onMessage: (event, ws) => {
      if (isReconnectRequest(event)) {
        ws.close(RECONNECT_CLOSE_CODE, 'The chat socket asked to reconnect');
        return;
      }
      dispatch(handleWebsocketMessageThunk({ event, user }));
    },
    onOpen: (event, ws) => {
      window.chatWebsocketRef = chatWebsocketRef;
      dispatch(setChatWebsocket(ws.url));

      // Resubscribe to all threads on reconnect
      if (chatThreadsRef.current.length > 0) {
        subscribeToChatThreads(ws, chatThreadsRef.current);
      }

      // The first connection is seeded by App's initial load; only a genuine
      // reconnect has a gap to reconcile.
      if (hasConnectedRef.current) {
        resyncAfterReconnect();
      }
      hasConnectedRef.current = true;
    },
    onClose: () => {
      delete window.chatWebsocketRef;
    },
  });

  const storeThreads = (threads) => {
    chatThreadsRef.current = threads;
  };

  return {
    chatWebsocketRef,
    storeThreads,
  };
};
